import { useState, useEffect, useCallback, Fragment } from "react";
import { getAuditLogs } from "../../../../Actions/AdminActions";
import { formatApiError } from "../../../../Actions/Utils";

const PAGE_SIZE = 20;

const ACTION_COLORS = {
  create: "bg-green-100 text-green-700",
  update: "bg-blue-100 text-blue-700",
  import: "bg-purple-100 text-purple-700",
  delete: "bg-red-100 text-red-700",
};

function ActionBadge({ action }) {
  const key = (action || "").toLowerCase().split("_")[0];
  const color = ACTION_COLORS[key] || "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {action || "—"}
    </span>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

function formatDate(str) {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return str;
  }
}

export default function AuditLog() {
  const [filters, setFilters] = useState({ user_id: "", action: "" });
  const [applied, setApplied] = useState({ user_id: "", action: "" });
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = useCallback(async (currentFilters, currentPage) => {
    setLoading(true);
    setError("");
    const res = await getAuditLogs({ ...currentFilters, page: currentPage, limit: PAGE_SIZE });
    setLoading(false);
    if (res?.status === 200) {
      setLogs(res.data?.items || res.data?.logs || res.data?.data || res.data?.results || []);
      const totalPages = res.data?.total_pages ?? Math.ceil((res.data?.total || 0) / PAGE_SIZE);
      setTotalPages(Math.max(1, totalPages));
    } else {
      setError(formatApiError(res?.data?.detail) || res?.data?.message || "Failed to load audit logs.");
    }
  }, []);

  useEffect(() => {
    fetchLogs(applied, page);
  }, [applied, page, fetchLogs]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setApplied({ ...filters });
  }

  function handleClear() {
    const empty = { user_id: "", action: "" };
    setFilters(empty);
    setApplied(empty);
    setPage(1);
  }

  function toggleExpand(id) {
    setExpanded((prev) => (prev === id ? null : id));
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Read-only record of all admin actions performed on users.
        </p>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">User ID</label>
            <input
              type="text"
              placeholder="Filter by user ID"
              value={filters.user_id}
              onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="">All actions</option>
              <option value="create_user">create_user</option>
              <option value="update_user">update_user</option>
              <option value="import_users">import_users</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            type="submit"
            className="flex-1 sm:flex-none px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="flex-1 sm:flex-none px-4 py-2 bg-white text-gray-600 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <Spinner />
        ) : logs.length === 0 ? (
          <div className="py-20 text-center text-gray-400 text-sm">
            No audit log entries found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {["Timestamp", "Action", "Status", "Admin ID", "Target Phone", "Summary", "Details"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log, i) => {
                  const id = log.id || log._id || i;
                  const isExpanded = expanded === id;
                  const hasDetails = log.payload_summary;

                  return (
                    <Fragment key={id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap font-mono">
                          {formatDate(log.created_at || log.timestamp)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <ActionBadge action={log.action} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            log.status === "success"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}>
                            {log.status || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 font-mono whitespace-nowrap">
                          {log.admin_user_id || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-mono whitespace-nowrap">
                          {log.target_phone || log.target_user_id || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                          {log.payload_summary
                            ? Object.entries(log.payload_summary)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(", ")
                            : log.summary || log.description || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {hasDetails ? (
                            <button
                              onClick={() => toggleExpand(id)}
                              className="text-xs text-indigo-600 hover:underline"
                            >
                              {isExpanded ? "Hide" : "View"}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                      {isExpanded && hasDetails && (
                        <tr>
                          <td colSpan={7} className="px-4 py-3 bg-gray-50">
                            <pre className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg p-3 overflow-x-auto max-h-48">
                              {JSON.stringify(log.payload_summary, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
